import React, { useState, useEffect } from 'react';
import {
  BookOpen, Plus, Search, Filter, Download, Eye, Edit,
  ChevronRight, ChevronDown, FileText, Calculator, CheckCircle,
  X, Calendar, User, History, TrendingUp, TrendingDown, DollarSign
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const GeneralLedgerTab = () => {
  const { language, t } = useLanguage();
  const [activeSubTab, setActiveSubTab] = useState('chart-of-accounts');
  const [expandedAccounts, setExpandedAccounts] = useState(new Set(['1', '1-1']));
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [selectedJournalEntry, setSelectedJournalEntry] = useState(null);
  const [showJournalDetail, setShowJournalDetail] = useState(false);
  const [selectedAccountForLedger, setSelectedAccountForLedger] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Listen for event to open journal entry form
  useEffect(() => {
    const handleOpenJournalEntry = () => {
      setActiveSubTab('journal-entries');
      setShowJournalForm(true);
    };
    window.addEventListener('openJournalEntryForm', handleOpenJournalEntry);
    return () => window.removeEventListener('openJournalEntryForm', handleOpenJournalEntry);
  }, []);

  const subTabs = [
    { id: 'chart-of-accounts', label: t('chartOfAccounts') },
    { id: 'journal-entries', label: t('journalEntries') },
    { id: 'trial-balance', label: t('trialBalance') },
    { id: 'ledger-entries', label: t('ledgerEntries') }
  ];

  // Chart of Accounts Tree Structure
  const chartOfAccounts = [
    {
      id: '1',
      code: '1000',
      name: 'Assets',
      type: 'Asset',
      balance: 12450000,
      children: [
        {
          id: '1-1',
          code: '1100',
          name: 'Current Assets',
          type: 'Asset',
          balance: 8900000,
          children: [
            { id: '1-1-1', code: '1110', name: 'Cash and Cash Equivalents', type: 'Asset', balance: 3890000 },
            { id: '1-1-2', code: '1120', name: 'Accounts Receivable', type: 'Asset', balance: 2145000 },
            { id: '1-1-3', code: '1130', name: 'Inventory', type: 'Asset', balance: 2865000 }
          ]
        },
        {
          id: '1-2',
          code: '1200',
          name: 'Fixed Assets',
          type: 'Asset',
          balance: 3550000,
          children: [
            { id: '1-2-1', code: '1210', name: 'Property, Plant & Equipment', type: 'Asset', balance: 3200000 },
            { id: '1-2-2', code: '1220', name: 'Accumulated Depreciation', type: 'Asset', balance: -650000 }
          ]
        }
      ]
    },
    {
      id: '2',
      code: '2000',
      name: 'Liabilities',
      type: 'Liability',
      balance: 8230000,
      children: [
        {
          id: '2-1',
          code: '2100',
          name: 'Current Liabilities',
          type: 'Liability',
          balance: 5890000,
          children: [
            { id: '2-1-1', code: '2110', name: 'Accounts Payable', type: 'Liability', balance: 1890000 },
            { id: '2-1-2', code: '2120', name: 'Short-term Debt', type: 'Liability', balance: 2500000 },
            { id: '2-1-3', code: '2130', name: 'Accrued Expenses', type: 'Liability', balance: 1500000 }
          ]
        },
        {
          id: '2-2',
          code: '2200',
          name: 'Long-term Liabilities',
          type: 'Liability',
          balance: 2340000,
          children: [
            { id: '2-2-1', code: '2210', name: 'Long-term Debt', type: 'Liability', balance: 2340000 }
          ]
        }
      ]
    },
    {
      id: '3',
      code: '3000',
      name: 'Equity',
      type: 'Equity',
      balance: 4220000,
      children: [
        { id: '3-1', code: '3100', name: 'Share Capital', type: 'Equity', balance: 2000000 },
        { id: '3-2', code: '3200', name: 'Retained Earnings', type: 'Equity', balance: 2220000 }
      ]
    },
    {
      id: '4',
      code: '4000',
      name: 'Revenue',
      type: 'Revenue',
      balance: 19500000,
      children: [
        { id: '4-1', code: '4100', name: 'Sales Revenue', type: 'Revenue', balance: 18000000 },
        { id: '4-2', code: '4200', name: 'Other Income', type: 'Revenue', balance: 1500000 }
      ]
    },
    {
      id: '5',
      code: '5000',
      name: 'Expenses',
      type: 'Expense',
      balance: 15200000,
      children: [
        { id: '5-1', code: '5100', name: 'Cost of Goods Sold', type: 'Expense', balance: 9500000 },
        { id: '5-2', code: '5200', name: 'Operating Expenses', type: 'Expense', balance: 4200000 },
        { id: '5-3', code: '5300', name: 'Depreciation Expense', type: 'Expense', balance: 1500000 }
      ]
    }
  ];

  const journalEntries = [
    {
      id: 'JE-2024-001',
      date: '2024-02-11',
      description: 'Monthly depreciation entry',
      reference: 'DEP-2024-02',
      status: 'Posted',
      total: 125000,
      entries: [
        { account: '5300 - Depreciation Expense', debit: 125000, credit: 0 },
        { account: '1220 - Accumulated Depreciation', debit: 0, credit: 125000 }
      ]
    },
    {
      id: 'JE-2024-002',
      date: '2024-02-10',
      description: 'Inventory adjustment',
      reference: 'INV-ADJ-001',
      status: 'Posted',
      total: 45000,
      entries: [
        { account: '1130 - Inventory', debit: 45000, credit: 0 },
        { account: '5100 - Cost of Goods Sold', debit: 0, credit: 45000 }
      ]
    },
    {
      id: 'JE-2024-003',
      date: '2024-02-09',
      description: 'Accrued expenses',
      reference: 'ACC-2024-02',
      status: 'Draft',
      total: 25000,
      entries: [
        { account: '2130 - Accrued Expenses', debit: 0, credit: 25000 },
        { account: '5200 - Operating Expenses', debit: 25000, credit: 0 }
      ]
    }
  ];

  const trialBalance = [
    { account: 'Cash and Cash Equivalents', code: '1110', debit: 3890000, credit: 0 },
    { account: 'Accounts Receivable', code: '1120', debit: 2145000, credit: 0 },
    { account: 'Inventory', code: '1130', debit: 2865000, credit: 0 },
    { account: 'Property, Plant & Equipment', code: '1210', debit: 3200000, credit: 0 },
    { account: 'Accumulated Depreciation', code: '1220', debit: 0, credit: 650000 },
    { account: 'Accounts Payable', code: '2110', debit: 0, credit: 1890000 },
    { account: 'Short-term Debt', code: '2120', debit: 0, credit: 2500000 },
    { account: 'Accrued Expenses', code: '2130', debit: 0, credit: 1500000 },
    { account: 'Long-term Debt', code: '2210', debit: 0, credit: 2340000 },
    { account: 'Share Capital', code: '3100', debit: 0, credit: 2000000 },
    { account: 'Retained Earnings', code: '3200', debit: 0, credit: 2220000 },
    { account: 'Sales Revenue', code: '4100', debit: 0, credit: 18000000 },
    { account: 'Other Income', code: '4200', debit: 0, credit: 1500000 },
    { account: 'Cost of Goods Sold', code: '5100', debit: 9500000, credit: 0 },
    { account: 'Operating Expenses', code: '5200', debit: 4200000, credit: 0 },
    { account: 'Depreciation Expense', code: '5300', debit: 1500000, credit: 0 }
  ];

  const toggleAccount = (id) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedAccounts(newExpanded);
  };

  // Get account history (mock data)
  const getAccountHistory = (accountCode) => {
    return [
      { date: '2024-02-11', entry: 'JE-2024-001', description: 'Monthly depreciation', debit: 0, credit: 125000, balance: 3890000 },
      { date: '2024-02-10', entry: 'JE-2024-002', description: 'Inventory adjustment', debit: 45000, credit: 0, balance: 3890000 },
      { date: '2024-02-09', entry: 'JE-2024-003', description: 'Accrued expenses', debit: 0, credit: 25000, balance: 3845000 },
      { date: '2024-02-08', entry: 'JE-2024-004', description: 'Sales revenue', debit: 0, credit: 185000, balance: 3845000 },
      { date: '2024-02-07', entry: 'JE-2024-005', description: 'Payment received', debit: 250000, credit: 0, balance: 3660000 },
    ];
  };

  const handleAccountClick = (account) => {
    if (!account.children || account.children.length === 0) {
      setSelectedAccount(account);
      setShowAccountModal(true);
    }
  };

  const renderAccountTree = (accounts, level = 0) => {
    return accounts.map((account) => {
      const hasChildren = account.children && account.children.length > 0;
      const isExpanded = expandedAccounts.has(account.id);
      const balanceColor = account.type === 'Asset' || account.type === 'Expense' 
        ? 'text-blue-600' 
        : 'text-green-600';

      return (
        <div key={account.id}>
          <div
            className={`flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg ${
              hasChildren ? 'cursor-pointer' : 'cursor-pointer'
            } ${level === 0 ? 'font-semibold' : ''} ${language === 'ar' ? 'flex-row-reverse' : ''}`}
            style={language === 'ar' 
              ? { paddingRight: `${level * 24 + 12}px` }
              : { paddingLeft: `${level * 24 + 12}px` }
            }
            onClick={(e) => {
              if (hasChildren) {
                toggleAccount(account.id);
              } else {
                handleAccountClick(account);
              }
            }}
          >
            {hasChildren && (
              <div className="w-4 h-4 flex items-center justify-center" onClick={(e) => {
                e.stopPropagation();
                toggleAccount(account.id);
              }}>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className={`h-4 w-4 text-gray-500 ${language === 'ar' ? 'rotate-180' : ''}`} />
                )}
              </div>
            )}
            {!hasChildren && (
              <div className="w-4 flex items-center justify-center">
                <Eye className="h-3 w-3 text-gray-400" />
              </div>
            )}
            <div className={`flex-1 grid grid-cols-4 gap-4 items-center ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <span className="text-sm font-mono text-gray-600">{account.code}</span>
              <span className="text-sm text-gray-900">{account.name}</span>
              <span className="text-xs text-gray-500">{account.type}</span>
              <div className={`flex items-center gap-2 ${language === 'ar' ? 'justify-start flex-row-reverse' : 'justify-end'}`}>
                <span className={`text-sm font-semibold ${balanceColor} ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                  SAR {account.balance.toLocaleString()}
                </span>
                {!hasChildren && (
                  <Eye className="h-4 w-4 text-gray-400 hover:text-blue-600" />
                )}
              </div>
            </div>
          </div>
          {hasChildren && isExpanded && (
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                {renderAccountTree(account.children, level + 1)}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      );
    });
  };

  const totalDebits = trialBalance.reduce((sum, account) => sum + account.debit, 0);
  const totalCredits = trialBalance.reduce((sum, account) => sum + account.credit, 0);
  const isBalanced = totalDebits === totalCredits;

  return (
    <div className={`space-y-6 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Sub-tabs */}
      <div className={`flex items-center gap-2 border-b border-gray-200 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Actions Bar */}
      <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-3 flex-1 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <div className={`relative flex-1 max-w-md ${language === 'ar' ? '' : ''}`}>
            <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                language === 'ar' ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'
              }`}
            />
          </div>
          <button 
            onClick={() => setShowFilterModal(true)}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Filter className="h-4 w-4" />
            {t('filter')}
          </button>
        </div>
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <button 
            onClick={() => alert(t('exportFunctionality'))}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Download className="h-4 w-4" />
            {t('export')}
          </button>
          {activeSubTab === 'journal-entries' && (
            <button 
              onClick={() => setShowJournalForm(true)}
              className={`px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
            >
              <Plus className="h-4 w-4" />
              {t('newJournalEntry')}
            </button>
          )}
        </div>
      </div>

      {/* Chart of Accounts */}
      {activeSubTab === 'chart-of-accounts' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className={`p-4 border-b border-gray-200 bg-gray-50 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <div className={`grid grid-cols-4 gap-4 text-xs font-semibold text-gray-600 uppercase ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <span>{t('accountCode')}</span>
              <span>{t('accountName')}</span>
              <span>{t('type')}</span>
              <span className={language === 'ar' ? 'text-left' : 'text-right'}>{t('balance')}</span>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {renderAccountTree(chartOfAccounts)}
          </div>
        </div>
      )}

      {/* Journal Entries */}
      {activeSubTab === 'journal-entries' && (
        <div className="space-y-4">
          {journalEntries
            .filter(entry => {
              if (filterType !== 'all' && entry.status !== filterType) return false;
              if (filterDateFrom && entry.date < filterDateFrom) return false;
              if (filterDateTo && entry.date > filterDateTo) return false;
              if (searchQuery && !entry.id.toLowerCase().includes(searchQuery.toLowerCase()) && 
                  !entry.description.toLowerCase().includes(searchQuery.toLowerCase()) &&
                  !entry.reference.toLowerCase().includes(searchQuery.toLowerCase())) return false;
              return true;
            })
            .map((entry) => (
            <div key={entry.id} className={`bg-white rounded-lg border border-gray-200 p-5 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <div className={`flex items-center justify-between mb-4 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div>
                  <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <h3 className="text-lg font-semibold text-gray-900">{entry.id}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      entry.status === 'Posted' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {entry.status === 'Posted' ? t('posted') : t('draft')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{entry.description}</p>
                  <div className={`flex items-center gap-4 mt-2 text-xs text-gray-500 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <span>{t('date')}: {entry.date}</span>
                    <span>{t('reference')}: {entry.reference}</span>
                    <span className="font-semibold text-gray-900">{t('total')}: SAR {entry.total.toLocaleString()}</span>
                  </div>
                </div>
                <div className={`flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <button 
                    onClick={() => {
                      setSelectedJournalEntry(entry);
                      setShowJournalDetail(true);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <Eye className="h-4 w-4 text-gray-600" />
                  </button>
                  {entry.status === 'Draft' && (
                    <button 
                      onClick={() => {
                        setSelectedJournalEntry(entry);
                        setShowJournalForm(true);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <Edit className="h-4 w-4 text-gray-600" />
                    </button>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                  <thead>
                    <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200">
                      <th className={`py-2 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('accountName')}</th>
                      <th className={`py-2 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('debit')}</th>
                      <th className={`py-2 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('credit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.entries.map((line, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="py-2 text-gray-900">{line.account}</td>
                        <td className={`py-2 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                          {line.debit > 0 ? `SAR ${line.debit.toLocaleString()}` : '-'}
                        </td>
                        <td className={`py-2 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                          {line.credit > 0 ? `SAR ${line.credit.toLocaleString()}` : '-'}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-semibold bg-white">
                      <td className="py-2">{t('total')}</td>
                      <td className={`py-2 text-blue-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                        SAR {entry.entries.reduce((sum, e) => sum + e.debit, 0).toLocaleString()}
                      </td>
                      <td className={`py-2 text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                        SAR {entry.entries.reduce((sum, e) => sum + e.credit, 0).toLocaleString()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Trial Balance */}
      {activeSubTab === 'trial-balance' && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className={`p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <h3 className="text-lg font-semibold text-gray-900">{t('trialBalance')}</h3>
            <div className={`flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              {isBalanced ? (
                <>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-600">{t('balanced')}</span>
                </>
              ) : (
                <>
                  <Calculator className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-medium text-red-600">{t('outOfBalance')}</span>
                </>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <thead>
                <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('accountCode')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('accountName')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('debit')}</th>
                  <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('credit')}</th>
                </tr>
              </thead>
              <tbody>
                {trialBalance.map((account, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono text-gray-600">{account.code}</td>
                    <td className="py-3 px-4 text-gray-900">{account.account}</td>
                    <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      {account.debit > 0 ? `SAR ${account.debit.toLocaleString()}` : '-'}
                    </td>
                    <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      {account.credit > 0 ? `SAR ${account.credit.toLocaleString()}` : '-'}
                    </td>
                  </tr>
                ))}
                <tr className="font-bold bg-gray-50 border-t-2 border-gray-300">
                  <td colSpan="2" className="py-3 px-4 text-gray-900">{t('total')}</td>
                  <td className={`py-3 px-4 text-blue-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {totalDebits.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {totalCredits.toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ledger Entries */}
      {activeSubTab === 'ledger-entries' && (
        <div className="bg-white rounded-lg border border-gray-200">
          {selectedAccountForLedger ? (
            <>
              <div className={`p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedAccountForLedger.name}</h3>
                  <p className="text-sm text-gray-600">{t('accountCode')}: {selectedAccountForLedger.code}</p>
                </div>
                <button
                  onClick={() => setSelectedAccountForLedger(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                >
                  {language === 'ar' ? 'اختر حساباً مختلفاً' : 'Select Different Account'}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                  <thead>
                    <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                      <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('date')}</th>
                      <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('entry')}</th>
                      <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('description')}</th>
                      <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('debit')}</th>
                      <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('credit')}</th>
                      <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('balance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getAccountHistory(selectedAccountForLedger.code).map((transaction, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-gray-600">{transaction.date}</td>
                        <td className="py-3 px-4">
                          <button className="text-blue-600 hover:text-blue-700 font-medium">
                            {transaction.entry}
                          </button>
                        </td>
                        <td className="py-3 px-4 text-gray-900">{transaction.description}</td>
                        <td className={`py-3 px-4 text-blue-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                          {transaction.debit > 0 ? `SAR ${transaction.debit.toLocaleString()}` : '-'}
                        </td>
                        <td className={`py-3 px-4 text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                          {transaction.credit > 0 ? `SAR ${transaction.credit.toLocaleString()}` : '-'}
                        </td>
                        <td className={`py-3 px-4 font-semibold text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                          SAR {transaction.balance.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className={`p-6 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
              <p className="text-gray-600 mb-4">{t('selectAccountFromChart')}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {chartOfAccounts.flatMap(account => 
                  account.children?.flatMap(child => 
                    child.children?.map(leaf => (
                      <button
                        key={leaf.id}
                        onClick={() => setSelectedAccountForLedger(leaf)}
                        className={`p-3 border border-gray-200 rounded-lg hover:bg-gray-50 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                      >
                        <p className="font-medium text-gray-900">{leaf.name}</p>
                        <p className="text-xs text-gray-500">{leaf.code}</p>
                      </button>
                    ))
                  ) || []
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Account Detail Modal */}
      <AnimatePresence>
        {showAccountModal && selectedAccount && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAccountModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className={`p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedAccount.name}</h2>
                    <div className={`flex items-center gap-4 mt-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                      <span className="text-sm font-mono text-gray-600">{t('code')}: {selectedAccount.code}</span>
                      <span className="text-sm text-gray-600">{t('type')}: {selectedAccount.type}</span>
                      <span className={`text-lg font-bold ${selectedAccount.type === 'Asset' || selectedAccount.type === 'Expense' ? 'text-blue-600' : 'text-green-600'}`}>
                        {t('balance')}: SAR {selectedAccount.balance.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAccountModal(false)}
                    className="p-2 hover:bg-white rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className={`flex-1 overflow-y-auto p-6 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className={`bg-blue-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('currentBalance')}</p>
                    <p className="text-2xl font-bold text-blue-600">
                      SAR {selectedAccount.balance.toLocaleString()}
                    </p>
                  </div>
                  <div className={`bg-green-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('totalDebitsMTD')}</p>
                    <p className="text-2xl font-bold text-green-600">
                      SAR {getAccountHistory(selectedAccount.code).reduce((sum, h) => sum + h.debit, 0).toLocaleString()}
                    </p>
                  </div>
                  <div className={`bg-orange-50 rounded-lg p-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <p className="text-sm text-gray-600 mb-1">{t('totalCreditsMTD')}</p>
                    <p className="text-2xl font-bold text-orange-600">
                      SAR {getAccountHistory(selectedAccount.code).reduce((sum, h) => sum + h.credit, 0).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Account History */}
                <div>
                  <h3 className={`text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    <History className="h-5 w-5" />
                    {t('transactionHistory')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('date')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('entry')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('description')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('debit')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('credit')}</th>
                          <th className={`py-3 px-4 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('balance')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getAccountHistory(selectedAccount.code).map((transaction, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-gray-600">{transaction.date}</td>
                            <td className="py-3 px-4">
                              <button className="text-blue-600 hover:text-blue-700 font-medium">
                                {transaction.entry}
                              </button>
                            </td>
                            <td className="py-3 px-4 text-gray-900">{transaction.description}</td>
                            <td className={`py-3 px-4 text-blue-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                              {transaction.debit > 0 ? `SAR ${transaction.debit.toLocaleString()}` : '-'}
                            </td>
                            <td className={`py-3 px-4 text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                              {transaction.credit > 0 ? `SAR ${transaction.credit.toLocaleString()}` : '-'}
                            </td>
                            <td className={`py-3 px-4 font-semibold text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                              SAR {transaction.balance.toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                <button
                  onClick={() => setShowAccountModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                >
                  {t('close')}
                </button>
                <button
                  onClick={() => {
                    setSelectedAccountForLedger(selectedAccount);
                    setShowAccountModal(false);
                    setActiveSubTab('ledger-entries');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                >
                  {t('viewFullLedger')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Journal Entry Form Modal */}
      <AnimatePresence>
        {showJournalForm && (
          <JournalEntryFormModal
            isOpen={showJournalForm}
            onClose={() => {
              setShowJournalForm(false);
              setSelectedJournalEntry(null);
            }}
            entry={selectedJournalEntry}
            onSave={(entry) => {
              // Mock save - add to journal entries
              console.log('Saving journal entry:', entry);
              setShowJournalForm(false);
              setSelectedJournalEntry(null);
              alert(t('journalEntrySaved'));
            }}
          />
        )}
      </AnimatePresence>

      {/* Journal Entry Detail Modal */}
      <AnimatePresence>
        {showJournalDetail && selectedJournalEntry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowJournalDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className={`p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedJournalEntry.id}</h2>
                    <div className={`flex items-center gap-4 mt-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        selectedJournalEntry.status === 'Posted' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {selectedJournalEntry.status === 'Posted' ? t('posted') : t('draft')}
                      </span>
                      <span className="text-sm text-gray-600">{t('date')}: {selectedJournalEntry.date}</span>
                      <span className="text-sm text-gray-600">{t('reference')}: {selectedJournalEntry.reference}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowJournalDetail(false)}
                    className="p-2 hover:bg-white rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              <div className={`flex-1 overflow-y-auto p-6 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">{t('description')}</h3>
                  <p className="text-gray-900">{selectedJournalEntry.description}</p>
                </div>

                <div>
                  <h3 className={`text-sm font-medium text-gray-600 mb-4 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                    {t('journalEntries')}
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                      <thead>
                        <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200">
                          <th className={`py-2 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('accountName')}</th>
                          <th className={`py-2 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('debit')}</th>
                          <th className={`py-2 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('credit')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedJournalEntry.entries.map((line, index) => (
                          <tr key={index} className="border-b border-gray-200">
                            <td className="py-2 text-gray-900">{line.account}</td>
                            <td className={`py-2 text-blue-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                              {line.debit > 0 ? `SAR ${line.debit.toLocaleString()}` : '-'}
                            </td>
                            <td className={`py-2 text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                              {line.credit > 0 ? `SAR ${line.credit.toLocaleString()}` : '-'}
                            </td>
                          </tr>
                        ))}
                        <tr className="font-semibold bg-white border-t-2 border-gray-300">
                          <td className="py-2">{t('total')}</td>
                          <td className={`py-2 text-blue-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                            SAR {selectedJournalEntry.entries.reduce((sum, e) => sum + e.debit, 0).toLocaleString()}
                          </td>
                          <td className={`py-2 text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                            SAR {selectedJournalEntry.entries.reduce((sum, e) => sum + e.credit, 0).toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                <button
                  onClick={() => setShowJournalDetail(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                >
                  {t('close')}
                </button>
                {selectedJournalEntry.status === 'Draft' && (
                  <button
                    onClick={() => {
                      setShowJournalDetail(false);
                      setShowJournalForm(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    {t('edit')}
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter Modal */}
      <AnimatePresence>
        {showFilterModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowFilterModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              <div className={`flex items-center justify-between mb-4 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
                <h2 className="text-xl font-bold text-gray-900">{t('filter')}</h2>
                <button onClick={() => setShowFilterModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
              <div className={`space-y-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('typeFilter')}</label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                  >
                    <option value="all">{t('allTypes')}</option>
                    <option value="Posted">{t('posted')}</option>
                    <option value="Draft">{t('draft')}</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('fromDate')}</label>
                    <input
                      type="date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('toDate')}</label>
                    <input
                      type="date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                    />
                  </div>
                </div>
                <div className={`flex items-center gap-3 pt-4 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                  <button
                    onClick={() => {
                      setFilterType('all');
                      setFilterDateFrom('');
                      setFilterDateTo('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                  >
                    {t('clear')}
                  </button>
                  <button
                    onClick={() => setShowFilterModal(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    {t('apply')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Journal Entry Form Modal Component
const JournalEntryFormModal = ({ isOpen, onClose, entry, onSave }) => {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    date: entry?.date || new Date().toISOString().split('T')[0],
    description: entry?.description || '',
    reference: entry?.reference || '',
    entries: entry?.entries?.map(e => ({
      account: e.account,
      debit: e.debit > 0 ? e.debit.toString() : '',
      credit: e.credit > 0 ? e.credit.toString() : ''
    })) || [
      { account: '', debit: '', credit: '' },
      { account: '', debit: '', credit: '' }
    ]
  });

  useEffect(() => {
    if (entry) {
      setFormData({
        date: entry.date,
        description: entry.description,
        reference: entry.reference,
        entries: entry.entries.map(e => ({
          account: e.account,
          debit: e.debit > 0 ? e.debit.toString() : '',
          credit: e.credit > 0 ? e.credit.toString() : ''
        }))
      });
    }
  }, [entry]);

  if (!isOpen) return null;

  const handleAddLine = () => {
    setFormData({
      ...formData,
      entries: [...formData.entries, { account: '', debit: '', credit: '' }]
    });
  };

  const handleSave = () => {
    // Validate entries balance
    const totalDebits = formData.entries.reduce((sum, e) => sum + parseFloat(e.debit || 0), 0);
    const totalCredits = formData.entries.reduce((sum, e) => sum + parseFloat(e.credit || 0), 0);
    
    if (Math.abs(totalDebits - totalCredits) > 0.01) {
      alert(t('journalEntryMustBalance'));
      return;
    }

    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className={`p-6 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <h2 className="text-2xl font-bold text-gray-900">{entry ? t('editJournalEntry') : t('newJournalEntry')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('date')}</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('reference')}</label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder="REF-2024-001"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={language === 'ar' ? 'أدخل الوصف...' : 'Enter description...'}
              rows={2}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            />
          </div>

          <div>
            <div className={`flex items-center justify-between mb-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
              <label className="block text-sm font-medium text-gray-700">{t('journalEntries')}</label>
              <button
                onClick={handleAddLine}
                className={`text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
              >
                <Plus className="h-4 w-4" />
                {t('addLine')}
              </button>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className={`py-2 px-3 font-semibold text-gray-700 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('accountName')}</th>
                    <th className={`py-2 px-3 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('debit')}</th>
                    <th className={`py-2 px-3 font-semibold text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('credit')}</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.entries.map((entryItem, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 px-3">
                        <input
                          type="text"
                          value={entryItem.account}
                          onChange={(e) => {
                            const newEntries = [...formData.entries];
                            newEntries[index].account = e.target.value;
                            setFormData({ ...formData, entries: newEntries });
                          }}
                          placeholder={language === 'ar' ? 'رمز الحساب أو الاسم' : 'Account code or name'}
                          className={`w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          value={entryItem.debit}
                          onChange={(e) => {
                            const newEntries = [...formData.entries];
                            newEntries[index].debit = e.target.value;
                            newEntries[index].credit = '';
                            setFormData({ ...formData, entries: newEntries });
                          }}
                          placeholder="0.00"
                          className={`w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${language === 'ar' ? 'text-left' : 'text-right'}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        <input
                          type="number"
                          value={entryItem.credit}
                          onChange={(e) => {
                            const newEntries = [...formData.entries];
                            newEntries[index].credit = e.target.value;
                            newEntries[index].debit = '';
                            setFormData({ ...formData, entries: newEntries });
                          }}
                          placeholder="0.00"
                          className={`w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 ${language === 'ar' ? 'text-left' : 'text-right'}`}
                        />
                      </td>
                      <td className="py-2 px-3">
                        {formData.entries.length > 2 && (
                          <button
                            onClick={() => {
                              const newEntries = formData.entries.filter((_, i) => i !== index);
                              setFormData({ ...formData, entries: newEntries });
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="py-2 px-3">{t('total')}</td>
                    <td className={`py-2 px-3 text-blue-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {formData.entries.reduce((sum, e) => sum + parseFloat(e.debit || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className={`py-2 px-3 text-green-600 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                      SAR {formData.entries.reduce((sum, e) => sum + parseFloat(e.credit || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
          >
            {t('cancel')}
          </button>
          <button
            onClick={() => {
              setFormData({ ...formData, status: 'Draft' });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
          >
            {t('saveAsDraft')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            {t('postEntry')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GeneralLedgerTab;
